import { useState, useRef } from "react";
import { useGSAP } from "@gsap/react";
import { MapSpots } from "../shared";
import { gsap } from "gsap";
import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { Heading3 } from "../typography";

export const TheChallenge = () => {
  const [activeButton, setActiveButton] = useState("manual");
  const jumpToManualRef = useRef<(() => void) | null>(null);
  const jumpToSatelliteRef = useRef<(() => void) | null>(null);
  const gradientBlobRef = useRef<HTMLDivElement>(null);

  useSectionNavTheme({
    trigger: "#the-challenge",
    isDark: false,
    start: "top top",
    end: "+=300%", // should be equal to mainTl + 100%
    // markers: true,
  });

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      gsap.set("path.manual, path.satellite", {
        scale: 0,
        transformOrigin: "center",
      });
      gsap.set(".nsw-border-path", { drawSVG: "0% 0%" });
      gsap.set(".orange-fill-semi-transparent", {
        scale: 0,
        opacity: 0.4,
        transformOrigin: "center",
      });

      // Set initial state for progress bars - start with 0 width
      gsap.set(".manual-progress-bar", { scaleX: 0, transformOrigin: "left" });
      gsap.set(".satellite-progress-bar", {
        scaleX: 0,
        transformOrigin: "left",
      });

      // Main timeline tied to scroll with progress tracking
      const totalScrollLength = 250; // Total scroll length in percentage
      const scrollForMap = 50; // Scroll length allocated to map animation
      const scrollForManualAndSatellite = totalScrollLength - scrollForMap; // Scroll length for text transitions

      const mapTl = gsap.timeline({
        ease: "none",
        scrollTrigger: {
          trigger: "#the-challenge",
          start: "top 50%",
          end: "top top",
          scrub: 1,
          invalidateOnRefresh: true,
          // markers: true,
          onUpdate: (self) => {
            const progress = self.progress;

            // Update active button based on scroll progress
            if (progress < scrollForMap / totalScrollLength) {
              // do nothing, still in map animation
            } else if (
              progress >=
              (totalScrollLength - scrollForManualAndSatellite) /
                totalScrollLength
            ) {
              setActiveButton("satellite");
            } else {
              setActiveButton("manual");
            }
          },
        },
      });
      mapTl
        .to(".nsw-border-path", {
          drawSVG: "0% 100%",
          ease: "power1.inOut",
        })
        .from(
          gradientBlobRef.current,
          {
            y: -1200,
            immediateRender: false,
          },
          "<"
        );

      const mainTl = gsap.timeline({
        scrollTrigger: {
          trigger: "#the-challenge",
          start: "top top",
          end: "+=200%",
          pin: "#the-challenge__content",
          scrub: 1,
          invalidateOnRefresh: true,
          // markers: true,
          onUpdate: (self) => {
            const progress = self.progress;

            if (progress <= 0.5) {
              const manualProgress = progress * 2;
              setActiveButton("manual");
              gsap.set(".manual-progress-bar", { scaleX: manualProgress });
              gsap.set(".satellite-progress-bar", { scaleX: 0 });
            } else if (progress > 0.5) {
              const satelliteProgress = (progress - 0.5) * 2;
              setActiveButton("satellite");
              gsap.set(".manual-progress-bar", { scaleX: 1 });
              gsap.set(".satellite-progress-bar", {
                scaleX: satelliteProgress,
              });
            }
          },
        },
      });

      let mainScrollTrigger = null;

      // THE STORY SEQUENCE
      mainTl
        // MANUAL PHASE: Show all manual dots (green + orange)
        .to("path.manual, .orange-fill-semi-transparent.one", {
          scale: 1,
          stagger: 0.05,
          duration: 5,
          ease: "back.out(1.7)",
          transformOrigin: "center",
          immediateRender: false,
        })

        .to(
          "path.manual.green-dot",
          {
            scale: 0,
            stagger: 0.1,
            duration: 4,
            ease: "power2.in",
            transformOrigin: "center",
            immediateRender: false,
          },
          "<0.9"
        )

        .to(
          "path.satellite, path.manual.green-dot, .orange-fill-semi-transparent:not(.one)",
          {
            scale: 1,
            stagger: 0.02,
            duration: 2,
            ease: "back.out(1.7)",
            transformOrigin: "center",
            immediateRender: false,
          }
        )
        .to(
          ".challenge",
          {
            opacity: 0,
            y: -720,
            duration: 3,
          },
          "<"
        )
        .to(
          ".solution",
          {
            opacity: 1,
            y: -520,
            duration: 2,
          },
          "<0.5"
        )

        // Hide non-issue green dots, keep only problem areas
        .to("path.green-dot", {
          opacity: 0,
          stagger: 0.02,
          ease: "power2.in",
          transformOrigin: "center",
        });

      mainScrollTrigger = mainTl.scrollTrigger;

      // Button click handlers
      const jumpToManual = () => {
        if (mainScrollTrigger) {
          const targetProgress = 0.2; // Middle of manual issue detection
          const scrollPosition = gsap.utils.interpolate(
            mainScrollTrigger.start,
            mainScrollTrigger.end,
            targetProgress
          );
          gsap.to(window, {
            scrollTo: scrollPosition,
            duration: 1,
            ease: "power2.inOut",
          });
        }
      };

      const jumpToSatellite = () => {
        if (mainScrollTrigger) {
          const targetProgress = 0.85; // Middle of satellite issue detection
          const scrollPosition = gsap.utils.interpolate(
            mainScrollTrigger.start,
            mainScrollTrigger.end,
            targetProgress
          );
          gsap.to(window, {
            scrollTo: scrollPosition,
            duration: 1,
            ease: "power2.inOut",
          });
        }
      };

      jumpToManualRef.current = jumpToManual;
      jumpToSatelliteRef.current = jumpToSatellite;
    });
  });

  return (
    <section id="the-challenge">
      <div
        id="the-challenge__content"
        className="relative bg-haizea-dark text-white md:h-screen py-16 lg:py-32 overflow-hidden"
      >
        <div
          ref={gradientBlobRef}
          className="gradient-blob absolute z-[0] pointer-events-none -left-[20vh]"
        ></div>
        <div className="container relative z-10 flex gap-[30px] md:gap-[10vw] xl:gap-[150px]">
          <div className="hidden md:block w-full md:w-1/2 xl:w-[608px] pt-10 lg:pt-20">
            <MapSpots className="md:max-h-[55vh]" />
            <div className="flex justify-center mt-4 gap-10 md:gap-20 text-2xl md:text-3xl lg:text-4xl">
              <div className="manual-button-wrapper">
                <button
                  className={`uppercase transition-opacity duration-300 cursor-pointer ${
                    activeButton === "manual" ? "active" : "opacity-35"
                  }`}
                  onClick={() => jumpToManualRef.current?.()}
                >
                  Manual
                </button>
                <div className="manual-progress-bar bg-red-400 h-0.5 w-full"></div>
              </div>
              <div className="satellite-button-wrapper">
                <button
                  className={`uppercase transition-opacity duration-300 cursor-pointer ${
                    activeButton === "satellite" ? "active" : "opacity-35"
                  }`}
                  onClick={() => jumpToSatelliteRef.current?.()}
                >
                  Satellite
                </button>
                <div className="satellite-progress-bar bg-amber-400 h-0.5 w-full"></div>
              </div>
            </div>
          </div>
          <div className="w-full flex flex-col gap-10 md:gap-32 md:w-1/2 md:pt-[15vh]">
            {/* Challenge Section - Shows during manual phase */}
            <div className="challenge">
              <Heading3 className="mb-6">The Challenge</Heading3>
              <div className="">
                <p className="text-lg md:text-xl leading-relaxed mb-4">
                  NSW&apos;s Natural Resources Access Regulator (NRAR) needed to
                  monitor water usage across thousands of agricultural
                  properties with limited resources.
                </p>
                <p className="text-lg md:text-xl leading-relaxed mb-4">
                  Traditional compliance methods - sending inspectors to
                  manually check meters - couldn&apos;t provide the
                  comprehensive coverage needed for effective water management
                  at scale.
                </p>
                <p className="text-lg md:text-xl leading-relaxed text-orange-300">
                  Manual inspection can only detect a few issues at a time...
                </p>
              </div>
            </div>

            {/* Solution Section - Shows during satellite phase */}
            <div className="solution md:opacity-0 xl:mt-[5vh]">
              <Heading3 className="mb-6">The Solution</Heading3>
              <p className="text-lg md:text-xl leading-relaxed mb-4">
                NRAR partnered with Haizea Analytics to develop monitoring tools
                using <strong>AI-enhanced Sentinel-2 satellite data</strong> and
                the
                <strong>Terrak.io platform</strong>
              </p>

              <p className="text-lg md:text-xl leading-relaxed mb-4">
                EXTRACTOR automatically screens properties for potential
                non-compliance by comparing what farmers report with satellite
                estimates of actual water usage.
              </p>

              <p className="text-lg md:text-xl leading-relaxed mb-4">
                WATER STORAGE EXPLORER monitors on-farm water storage using
                LiDAR data across over 1,200 storage facilities.
              </p>

              <p className="text-lg md:text-xl leading-relaxed text-green-300">
                Satellite monitoring reveals the full scope of issues across the
                entire region.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
